<script setup lang="ts">
import type { Schemas } from "#shopware";

const props = defineProps<{
  product: Schemas["Product"];
  quantity: number;
  hidePrice?: boolean;
}>();

const { quantity } = toRefs(props);

const emits = defineEmits<{
  (
    e: "select",
    product: {
      id: string | undefined;
      quantity: number;
      cover: Schemas["Media"] | undefined;
      productNumber: string;
      name: string;
      unitPrice: number | undefined;
      type: string;
    }
  ): void;
  (e: "remove", id: string): void;
}>();

const { product } = toRefs(props);

const { unitPrice } = useProductPrice(product);

const selected = ref(false);

const selectProduct = () => {
  const isProductSelected = selected.value;
  const productId = product.value.id;
  const payload = {
    id: productId,
    quantity: quantity.value,
    cover: product.value?.cover?.media,
    name: product.value?.translated?.name,
    unitPrice: unitPrice.value,
    productNumber: product.value?.productNumber,
    type: "product",
  };

  if (isProductSelected) {
    emits("select", payload);
  } else {
    emits("remove", productId);
  }
};

onUnmounted(() => {
  selected.value = false;
});
</script>
<template>
  <div class="group relative flex py-5 border-skin-primary">
    <div
      class="relative w-11 flex justify-start items-center border-r border-skin-primary"
    >
      <input
        type="checkbox"
        v-model="selected"
        class="absolute w-full h-full opacity-0 cursor-pointer [&:checked+label]:text-sm [&:checked+label]:bg-skin-theme"
        @change="selectProduct"
      />
      <label
        class="block w-6 h-6 border border-skin-light rounded text-0 text-center py-0.5 text-skin-white"
      >
        <i class="fa-solid fa-check"></i>
      </label>
    </div>
    <div class="flex items-center flex-1">
      <div class="w-[3.75rem] h-[3.75rem]">
        <NuxtImg
          format="webp"
          loading="lazy"
          :src="product?.cover?.media?.url"
          :alt="product?.translated?.name"
          :width="product?.cover?.media?.metaData?.width"
          :height="product?.cover?.media?.metaData?.height"
          class="object-contain h-full w-full"
        />
      </div>
      <div class="flex mobile:gap-y-2 flex-wrap flex-1 pl-2.5">
        <div
          class="md:w-3/5 md:px-5 flex flex-col justify-center md:border-r border-skin-primary md:py-4"
        >
          <h5 class="font-bold text-md mb-2">
            {{ product?.translated?.name }}
          </h5>
          <!-- <p
            v-html="product?.translated?.description"
            class="whitespace-nowrap overflow-hidden text-ellipsis line-clamp-2 max-w-fit"
          ></p> -->
        </div>
        <div
          class="md:w-3/12 md:px-5 flex justify-center items-center md:border-r border-skin-primary md:py-4"
        >
          <p>{{ $t("product.productNumber") }}: {{ product?.productNumber }}</p>
        </div>
        <div
          class="md:w-[15%] md:px-5 flex items-center justify-center md:py-4"
        >
          <SharedPrice
            v-if="!hidePrice"
            :value="unitPrice * quantity"
            class="font-bold text-base"
          />
        </div>
      </div>
    </div>
  </div>
</template>
